/**
 * Lecture et écriture de CSV côté navigateur.
 *
 * L'import de stock est le socle de tout le produit : sans une ligne en stock, l'écran 01A de
 * l'application mobile ne peut RIEN valider et aucun appareil ne peut être vendu. Le fichier
 * vient d'une chaîne de production, pas d'un formulaire : il est donc analysé et contrôlé ligne
 * par ligne DANS LE NAVIGATEUR avant tout appel réseau, pour que l'opérateur usine corrige son
 * fichier au lieu de découvrir 400 rejets serveur.
 *
 * Le contrôle local applique les mêmes règles que `@genviz/shared/validation` ; le serveur reste
 * la source de vérité et refait tout (unicité globale comprise, qu'un navigateur ne peut pas voir).
 */

import { ModeleAppareil } from '@genviz/shared/enums';
import {
  LONGUEUR_MAX,
  RE_CLE_APPAIRAGE,
  RE_NUMERO_SERIE,
  normaliserNumeroSerie,
} from '@genviz/shared/validation';
import type { LigneImportStock } from '@genviz/shared/dto';

/* -------------------------------------------------------------------------- */
/* Analyse lexicale                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Découpe un CSV en matrice de chaînes. Gère les guillemets RFC 4180 (`""` échappe un
 * guillemet), les fins de ligne CRLF/LF et les champs multilignes.
 */
export function decouperCsv(texte: string, separateur: string): string[][] {
  const lignes: string[][] = [];
  let ligne: string[] = [];
  let champ = '';
  let entreGuillemets = false;
  let i = 0;

  // Retire un éventuel BOM UTF-8 laissé par Excel.
  const source = texte.charCodeAt(0) === 0xfeff ? texte.slice(1) : texte;

  const finDeChamp = (): void => {
    ligne.push(champ);
    champ = '';
  };
  const finDeLigne = (): void => {
    finDeChamp();
    lignes.push(ligne);
    ligne = [];
  };

  while (i < source.length) {
    const c = source[i] as string;

    if (entreGuillemets) {
      if (c === '"') {
        if (source[i + 1] === '"') {
          champ += '"';
          i += 2;
          continue;
        }
        entreGuillemets = false;
        i += 1;
        continue;
      }
      champ += c;
      i += 1;
      continue;
    }

    if (c === '"' && champ === '') {
      entreGuillemets = true;
      i += 1;
      continue;
    }
    if (c === separateur) {
      finDeChamp();
      i += 1;
      continue;
    }
    if (c === '\r') {
      i += 1;
      continue;
    }
    if (c === '\n') {
      finDeLigne();
      i += 1;
      continue;
    }
    champ += c;
    i += 1;
  }

  if (champ !== '' || ligne.length > 0) finDeLigne();

  return lignes.filter((l) => l.some((v) => v.trim() !== ''));
}

/** Devine le séparateur d'après la première ligne : `;` (export Excel FR), `,` ou tabulation. */
export function devinerSeparateur(texte: string): string {
  const premiere = texte.split(/\r?\n/, 1)[0] ?? '';
  const compte = (c: string): number => premiere.split(c).length - 1;
  const candidats: Array<[string, number]> = [
    [';', compte(';')],
    [',', compte(',')],
    ['\t', compte('\t')],
  ];
  candidats.sort((a, b) => b[1] - a[1]);
  const meilleur = candidats[0];
  return meilleur && meilleur[1] > 0 ? meilleur[0] : ';';
}

/* -------------------------------------------------------------------------- */
/* Colonnes attendues                                                          */
/* -------------------------------------------------------------------------- */

/** Normalise un en-tête : minuscules, sans accents, séparateurs unifiés en `_`. */
function cleEntete(brut: string): string {
  return brut
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/gu, '')
    .trim()
    .toLowerCase()
    .replace(/[\s\-.]+/gu, '_');
}

interface DefinitionColonne {
  cle: keyof LigneImportStock;
  alias: string[];
  obligatoire: boolean;
  intitule: string;
}

/** Colonnes du CSV de production, avec les intitulés tolérés en en-tête. */
export const COLONNES_IMPORT: readonly DefinitionColonne[] = [
  {
    cle: 'numero_serie_electrificateur',
    intitule: 'N° de série électrificateur',
    alias: ['numero_serie_electrificateur', 'sn_electrificateur', 'serial', 'numero_serie'],
    obligatoire: true,
  },
  {
    cle: 'numero_serie_module',
    intitule: 'N° de série module',
    alias: ['numero_serie_module', 'sn_module', 'module', 'serial_module'],
    obligatoire: true,
  },
  {
    cle: 'modele',
    intitule: 'Modèle',
    alias: ['modele', 'model', 'type'],
    obligatoire: true,
  },
  {
    cle: 'secret_appairage',
    intitule: 'Secret d’appairage',
    alias: ['secret_appairage', 'secret', 'cle_appairage', 'key', 'pairing_key'],
    obligatoire: true,
  },
  { cle: 'imei', intitule: 'IMEI', alias: ['imei'], obligatoire: false },
  { cle: 'iccid', intitule: 'ICCID', alias: ['iccid', 'sim'], obligatoire: false },
  { cle: 'msisdn', intitule: 'MSISDN', alias: ['msisdn', 'numero_sim'], obligatoire: false },
  { cle: 'lot', intitule: 'Lot', alias: ['lot', 'batch', 'lot_production'], obligatoire: false },
  {
    cle: 'date_fabrication',
    intitule: 'Date de fabrication',
    alias: ['date_fabrication', 'date_fab', 'manufactured_at'],
    obligatoire: false,
  },
  {
    cle: 'firmware_usine',
    intitule: 'Firmware usine',
    alias: ['firmware_usine', 'firmware', 'fw'],
    obligatoire: false,
  },
  {
    cle: 'empreinte_certificat',
    intitule: 'Empreinte du certificat',
    alias: ['empreinte_certificat', 'certificat', 'fingerprint', 'sha256'],
    obligatoire: false,
  },
];

/** Modèle de fichier proposé au téléchargement depuis l'écran d'import. */
export const ENTETE_MODELE_CSV = COLONNES_IMPORT.map((c) => c.cle).join(';');

/** Exemple complet, pour que l'opérateur voie le format attendu de chaque colonne. */
export const EXEMPLE_MODELE_CSV = [
  ENTETE_MODELE_CSV,
  [
    'GVZ-2026-000101',
    'MOD-8891001',
    ModeleAppareil.PORTABLE_SOLAIRE,
    'K7M2P9QD',
    '350077000000101',
    '8933150000000000101',
    '',
    'LOT-2026-07',
    '2026-07-12',
    '1.4.2',
    '',
  ].join(';'),
].join('\n');

/* -------------------------------------------------------------------------- */
/* Contrôle ligne par ligne                                                    */
/* -------------------------------------------------------------------------- */

export interface ErreurChampCsv {
  champ: keyof LigneImportStock | 'ligne';
  message: string;
}

export interface LigneAnalysee {
  /** Index 1-based dans le lot soumis, hors ligne d'en-tête — identique à l'index serveur. */
  numero: number;
  valeurs: LigneImportStock;
  erreurs: ErreurChampCsv[];
}

export interface AnalyseCsv {
  separateur: string;
  /** En-têtes présents dans le fichier mais inconnus du contrat : ignorés, signalés. */
  colonnesInconnues: string[];
  /** Colonnes obligatoires absentes de l'en-tête : l'import est alors impossible. */
  colonnesManquantes: string[];
  lignes: LigneAnalysee[];
  nbValides: number;
  /** Erreur bloquante empêchant toute analyse (fichier vide, en-tête absent…). */
  erreurGlobale: string | null;
}

const RE_IMEI = /^\d{15}$/u;
const RE_ICCID = /^\d{18,20}$/u;
const RE_MSISDN = /^\+?\d{6,15}$/u;
const RE_DATE = /^\d{4}-\d{2}-\d{2}$/u;
const RE_EMPREINTE = /^[0-9a-fA-F]{64}$/u;
const RE_FIRMWARE = /^[0-9A-Za-z][0-9A-Za-z.\-_]*$/u;

const MODELES = new Set<string>(Object.values(ModeleAppareil));

function controlerSerie(
  valeur: string,
  champ: 'numero_serie_electrificateur' | 'numero_serie_module',
  erreurs: ErreurChampCsv[],
): string {
  const normalise = normaliserNumeroSerie(valeur);
  if (normalise === '') {
    erreurs.push({ champ, message: 'Numéro de série obligatoire.' });
    return '';
  }
  if ([...normalise].length > LONGUEUR_MAX.NUMERO_SERIE) {
    erreurs.push({
      champ,
      message: `Numéro de série trop long (${LONGUEUR_MAX.NUMERO_SERIE} caractères maximum).`,
    });
  } else if (!RE_NUMERO_SERIE.test(normalise)) {
    erreurs.push({
      champ,
      message: 'Caractères non autorisés : lettres, chiffres, tiret, souligné et espace.',
    });
  }
  return normalise;
}

/**
 * Analyse un CSV d'entrée en stock et contrôle chaque ligne.
 * Aucune ligne n'est écartée : une ligne fautive est conservée avec ses erreurs pour être
 * affichée à l'opérateur, qui corrige son fichier et recharge.
 */
export function analyserCsvStock(texte: string): AnalyseCsv {
  const separateur = devinerSeparateur(texte);
  const matrice = decouperCsv(texte, separateur);

  if (matrice.length === 0) {
    return {
      separateur,
      colonnesInconnues: [],
      colonnesManquantes: [],
      lignes: [],
      nbValides: 0,
      erreurGlobale: 'Fichier vide.',
    };
  }

  const entetes = (matrice[0] ?? []).map(cleEntete);
  const indexParCle = new Map<keyof LigneImportStock, number>();
  const reconnues = new Set<number>();

  for (const colonne of COLONNES_IMPORT) {
    const index = entetes.findIndex((e) => colonne.alias.includes(e));
    if (index >= 0) {
      indexParCle.set(colonne.cle, index);
      reconnues.add(index);
    }
  }

  const colonnesInconnues = entetes.filter((e, i) => e !== '' && !reconnues.has(i));
  const colonnesManquantes = COLONNES_IMPORT.filter(
    (c) => c.obligatoire && !indexParCle.has(c.cle),
  ).map((c) => c.intitule);

  if (colonnesManquantes.length > 0) {
    return {
      separateur,
      colonnesInconnues,
      colonnesManquantes,
      lignes: [],
      nbValides: 0,
      erreurGlobale: `Colonnes obligatoires absentes de l’en-tête : ${colonnesManquantes.join(', ')}.`,
    };
  }

  const champ = (ligne: string[], cle: keyof LigneImportStock): string => {
    const index = indexParCle.get(cle);
    if (index === undefined) return '';
    return (ligne[index] ?? '').trim();
  };

  const lignes: LigneAnalysee[] = [];
  const vuesElectrificateur = new Map<string, number>();
  const vuesModule = new Map<string, number>();

  for (let i = 1; i < matrice.length; i += 1) {
    const brute = matrice[i] ?? [];
    const erreurs: ErreurChampCsv[] = [];

    const snElectrificateur = controlerSerie(
      champ(brute, 'numero_serie_electrificateur'),
      'numero_serie_electrificateur',
      erreurs,
    );
    const snModule = controlerSerie(
      champ(brute, 'numero_serie_module'),
      'numero_serie_module',
      erreurs,
    );

    const modeleBrut = champ(brute, 'modele').toUpperCase().replace(/[\s-]+/gu, '_');
    if (!MODELES.has(modeleBrut)) {
      erreurs.push({
        champ: 'modele',
        message: `Modèle inconnu. Valeurs acceptées : ${[...MODELES].join(', ')}.`,
      });
    }

    const secret = champ(brute, 'secret_appairage').toUpperCase();
    if (secret === '') {
      erreurs.push({ champ: 'secret_appairage', message: 'Secret d’appairage obligatoire.' });
    } else if (!RE_CLE_APPAIRAGE.test(secret)) {
      erreurs.push({
        champ: 'secret_appairage',
        message:
          '8 caractères parmi 0-9 et A-Z sans les lettres ambiguës I, L, O et U (alphabet du QR code).',
      });
    }

    const imei = champ(brute, 'imei');
    if (imei !== '' && !RE_IMEI.test(imei)) {
      erreurs.push({ champ: 'imei', message: 'IMEI attendu : 15 chiffres.' });
    }

    const iccid = champ(brute, 'iccid');
    if (iccid !== '' && !RE_ICCID.test(iccid)) {
      erreurs.push({ champ: 'iccid', message: 'ICCID attendu : 18 à 20 chiffres.' });
    }

    const msisdn = champ(brute, 'msisdn');
    if (msisdn !== '' && !RE_MSISDN.test(msisdn)) {
      erreurs.push({ champ: 'msisdn', message: 'MSISDN attendu au format international.' });
    }

    const lot = champ(brute, 'lot');
    if ([...lot].length > 50) {
      erreurs.push({ champ: 'lot', message: 'Lot : 50 caractères maximum.' });
    }

    const dateFabrication = champ(brute, 'date_fabrication');
    if (dateFabrication !== '') {
      if (!RE_DATE.test(dateFabrication) || Number.isNaN(Date.parse(dateFabrication))) {
        erreurs.push({ champ: 'date_fabrication', message: 'Date attendue au format AAAA-MM-JJ.' });
      }
    }

    const firmware = champ(brute, 'firmware_usine');
    if (firmware !== '') {
      if ([...firmware].length > LONGUEUR_MAX.FIRMWARE_VERSION) {
        erreurs.push({
          champ: 'firmware_usine',
          message: `Version trop longue (${LONGUEUR_MAX.FIRMWARE_VERSION} caractères maximum).`,
        });
      } else if (!RE_FIRMWARE.test(firmware)) {
        erreurs.push({ champ: 'firmware_usine', message: 'Version de firmware invalide.' });
      }
    }

    const empreinte = champ(brute, 'empreinte_certificat');
    if (empreinte !== '' && !RE_EMPREINTE.test(empreinte)) {
      erreurs.push({
        champ: 'empreinte_certificat',
        message: 'Empreinte SHA-256 attendue : 64 caractères hexadécimaux.',
      });
    }

    const numero = lignes.length + 1;

    // Doublons INTERNES au fichier : le serveur ne verrait que le premier passage et rejetterait
    // le second sans expliquer d'où il vient.
    if (snElectrificateur !== '') {
      const precedente = vuesElectrificateur.get(snElectrificateur);
      if (precedente !== undefined) {
        erreurs.push({
          champ: 'numero_serie_electrificateur',
          message: `Doublon dans le fichier (déjà en ligne ${precedente}).`,
        });
      } else {
        vuesElectrificateur.set(snElectrificateur, numero);
      }
    }
    if (snModule !== '') {
      const precedente = vuesModule.get(snModule);
      if (precedente !== undefined) {
        erreurs.push({
          champ: 'numero_serie_module',
          message: `Doublon dans le fichier (déjà en ligne ${precedente}).`,
        });
      } else {
        vuesModule.set(snModule, numero);
      }
    }

    const valeurs: LigneImportStock = {
      numero_serie_electrificateur: snElectrificateur,
      numero_serie_module: snModule,
      modele: (MODELES.has(modeleBrut)
        ? modeleBrut
        : ModeleAppareil.PORTABLE_SOLAIRE) as ModeleAppareil,
      secret_appairage: secret,
      ...(imei !== '' ? { imei } : {}),
      ...(iccid !== '' ? { iccid } : {}),
      ...(msisdn !== '' ? { msisdn } : {}),
      ...(lot !== '' ? { lot } : {}),
      ...(dateFabrication !== '' ? { date_fabrication: dateFabrication } : {}),
      ...(firmware !== '' ? { firmware_usine: firmware } : {}),
      ...(empreinte !== '' ? { empreinte_certificat: empreinte.toLowerCase() } : {}),
    };

    lignes.push({ numero, valeurs, erreurs });
  }

  return {
    separateur,
    colonnesInconnues,
    colonnesManquantes,
    lignes,
    nbValides: lignes.filter((l) => l.erreurs.length === 0).length,
    erreurGlobale: lignes.length === 0 ? 'Aucune ligne de données sous l’en-tête.' : null,
  };
}

/* -------------------------------------------------------------------------- */
/* Écriture                                                                    */
/* -------------------------------------------------------------------------- */

function echapper(valeur: string | number | boolean | null | undefined): string {
  if (valeur === null || valeur === undefined) return '';
  const texte = String(valeur);
  return /[";\n\r]/u.test(texte) ? `"${texte.replace(/"/gu, '""')}"` : texte;
}

/** Sérialise un tableau en CSV point-virgule (le séparateur attendu par Excel en français). */
export function construireCsv(
  entetes: readonly string[],
  lignes: ReadonlyArray<ReadonlyArray<string | number | boolean | null | undefined>>,
): string {
  const corps = lignes.map((ligne) => ligne.map(echapper).join(';'));
  return [entetes.map(echapper).join(';'), ...corps].join('\r\n');
}
